/**
 * LoginPage — dedicated /login route.
 *
 * Wraps the existing <Login /> component in a route-aware shell. Watches
 * Firebase auth state; the moment sign-in succeeds we swap to a loading
 * screen while the user's profile is fetched, then navigate to their
 * admin-set `defaultModule` (falling back to /map).
 *
 * Why the loading screen instead of navigating immediately: without it,
 * users with a non-map default saw /map mount for ~1s before AuthGate's
 * one-shot redirect fired. The loading screen holds the app in the
 * auth shell until the Firestore read for `preferences.defaultModule`
 * resolves (sub-second on a warm Firestore), then routes once. Zero
 * flash of the wrong module.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { Login } from './Login';
import { MODULE_ROUTES, type DefaultModule } from '../types/preferences';

interface LocationState {
  // If a user was bounced here by AuthGate, react-router puts the original
  // path on location.state. We honor that so they land back where they
  // were trying to go after a successful login.
  from?: string;
}

type AuthStage =
  | 'checking'       // mounting; haven't asked Firebase yet
  | 'anonymous'      // Firebase said no user; show the login form
  | 'resolving'      // user is signed in; fetching profile → preferences
  | 'navigating';    // profile fetched; replace() in flight

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<AuthStage>('checking');
  const [resolvingError, setResolvingError] = useState<string | null>(null);

  const explicitDestination = (location.state as LocationState | null)?.from || null;

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    // signInWithEmailAndPassword fires onAuthStateChanged more than once
    // (base sign-in, then token refresh). Latch so we only resolve the
    // destination ONCE per mount — repeated navigate() calls would be
    // throttled by Chrome and the actual route change would never land.
    let hasResolved = false;

    /** Fetch profile, pick landing route, navigate. Explicit `from`
     *  always wins. On any failure we fall back to /map so a Firestore
     *  blip doesn't trap the user on the loading screen. */
    const resolveAndNavigate = async () => {
      if (hasResolved || cancelled) return;
      hasResolved = true;
      setStage('resolving');
      setResolvingError(null);

      // Explicit intent wins — no profile fetch needed.
      if (explicitDestination) {
        setStage('navigating');
        navigate(explicitDestination, { replace: true });
        return;
      }

      let target = '/map';
      try {
        const auth = await getFirebaseAuth();
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const res = await fetch('/api/user/profile', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const mod = data?.preferences?.defaultModule as DefaultModule | undefined;
            if (mod === 'custom_ui') {
              // Custom UI resolves to a specific saved doc, not a static
              // route. If the user chose this module but no UI id is set
              // (or the id was deleted), fall back to /map — a one-time
              // toast after navigation tells them what happened.
              const ref = data?.preferences?.defaultCustomUi as { uiId?: string } | undefined;
              if (ref?.uiId) {
                target = `/custom-ui/${encodeURIComponent(ref.uiId)}`;
              } else {
                try { sessionStorage.setItem('customUiLandingToast', 'missing-ref'); } catch {}
              }
            } else if (mod && MODULE_ROUTES[mod]) {
              target = MODULE_ROUTES[mod];
            }
          }
        }
      } catch (err) {
        // Non-fatal — still send them to /map. We log but don't surface
        // the error to the screen; a flash of "profile load failed" on
        // every sign-in would be worse than the silent fallback.
        console.warn('[LoginPage] profile fetch failed, falling back to /map:', err);
      }

      if (cancelled) return;
      setStage('navigating');
      navigate(target, { replace: true });
    };

    (async () => {
      const auth = await getFirebaseAuth();
      if (cancelled) return;

      // Already signed in (direct hit on /login with an active session).
      if (auth.currentUser) {
        void resolveAndNavigate();
        return;
      }

      setStage('anonymous');
      unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) void resolveAndNavigate();
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
    // Deliberate empty deps — navigate / explicitDestination are stable
    // for the lifetime of this mount. Re-running the subscription would
    // double-fire the navigate() and tangle with Chrome's throttle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared chrome for every non-login state so the backdrop doesn't
  // flash while we transition between stages.
  const splashBg: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    background: 'linear-gradient(135deg, #00203F 0%, #004777 50%, #006B88 100%)',
    color: '#e2e8f0',
  };

  // Not sure yet whether we have a user — brief in practice, but we
  // render SOMETHING so there's no blank frame.
  if (stage === 'checking') {
    return <div style={splashBg}><div>Loading…</div></div>;
  }

  // Signed in, holding here until the profile fetch picks a landing
  // route. On a warm Firestore this is sub-second — the user sees a
  // spinner, not a flash of the wrong module.
  if (stage === 'resolving' || stage === 'navigating') {
    return (
      <div style={splashBg}>
        <Spinner />
        <div style={{ fontSize: 14, color: 'rgba(226,232,240,0.85)' }}>
          Loading your workspace…
        </div>
        {resolvingError && (
          <div style={{ fontSize: 12, color: '#fecaca', maxWidth: 320, textAlign: 'center' }}>
            {resolvingError}
          </div>
        )}
      </div>
    );
  }

  // stage === 'anonymous' — show the login form.
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #00203F 0%, #004777 50%, #006B88 100%)',
      padding: 24,
    }}>
      <Login />
    </div>
  );
};

/** Small CSS spinner — avoids pulling in another animation library
 *  just for the one-screen loading state. */
const Spinner: React.FC = () => (
  <>
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      border: '3px solid rgba(255,255,255,0.15)',
      borderTopColor: '#5eead4',
      animation: 'mosaics-login-spin 0.9s linear infinite',
    }} />
    <style>{`
      @keyframes mosaics-login-spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </>
);

export default LoginPage;
